import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import type { Agendamiento } from '@/shared/interfaces';
import { EstadosOrdenTrabajoEnumChoice } from '@/shared';
import { useFetchOrdenTrabajos } from '@/shared/actions/erp';
import VisitaFinalizadaDetalle from '@/erp/orden-servicio/orden-servicio-visita/components/visitas-finalizadas/VisitaFinalizadaDetalle';
import TicketDetailContent from '@/erp/tickets-clientes/detalle-ticket/components/TicketDetailContent';
import {
  estadoVisitaAgenda,
  ESTADO_AGENDA_COLOR,
  ESTADO_AGENDA_LABEL,
} from '../estadoVisitaAgenda';

interface Props {
  open: boolean;
  onClose: () => void;
  agendamiento: Agendamiento | null;
}

/** Estado de panel (carga / vacío): mensaje útil, nunca pantalla en blanco. */
function PanelState({
  icon,
  title,
  msg,
  busy,
}: {
  icon: React.ReactNode;
  title: string;
  msg?: string;
  busy?: boolean;
}) {
  return (
    <Box
      role="status"
      aria-busy={busy ? true : undefined}
      sx={{
        minHeight: 320,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        p: 4,
        color: 'text.secondary',
      }}
    >
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
      {msg && (
        <Typography variant="body2" sx={{ maxWidth: 440 }}>
          {msg}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Pestaña "Calidad de Visita": resuelve la Orden de Trabajo REALIZADA por
 * id_ticket (mismo vínculo que usa el detalle del ticket) y embebe la vista
 * completa de Calidad de Visitas. Solo se monta cuando la pestaña está activa.
 */
function CalidadVisitaPanel({ idTicket }: { idTicket: string }) {
  const { data, isLoading } = useFetchOrdenTrabajos({
    params: {
      id_ticket: idTicket,
      estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.REALIZADA,
    },
  });
  const ot = (data as any)?.data?.data?.[0];

  if (isLoading) {
    return (
      <PanelState
        busy
        icon={<CircularProgress />}
        title="Cargando calidad de la visita…"
      />
    );
  }
  if (!ot) {
    return (
      <PanelState
        icon={<FactCheckIcon sx={{ fontSize: 56, opacity: 0.35 }} />}
        title="Visita aún no finalizada"
        msg="La calidad de la visita se mostrará cuando el técnico finalice su orden de trabajo (estado REALIZADA)."
      />
    );
  }
  return <VisitaFinalizadaDetalle ot={ot} />;
}

/**
 * Modal de detalle de una visita de la Agenda de Técnicos. Se abre al hacer
 * clic en el nombre de la flota/técnico y muestra 2 pestañas:
 *   1) Calidad de Visita (vista de /operaciones/visitas embebida)
 *   2) Ticket (detalle del ticket de soporte ligado, solo lectura)
 */
export default function ModalDetalleVisitaAgenda({ open, onClose, agendamiento }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);

  const ag = agendamiento as any;

  // Al abrir otra visita, siempre iniciar en la 1ª pestaña.
  useEffect(() => {
    setTab(0);
  }, [ag?.id_agendamiento, open]);

  const idTicket = ag?.id_ticket ? String(ag.id_ticket) : '';
  const tag = ag ? estadoVisitaAgenda(ag) : 'no_iniciado';
  const color = ESTADO_AGENDA_COLOR[tag];
  const estadoLabel = ESTADO_AGENDA_LABEL[tag];
  const tecnico = ag?.flota_data?.nombre || 'Sin técnico';
  const cliente =
    ag?.prospecto_data?.razon_social ||
    ag?.contrato_data?.cliente_data?.razon_social ||
    ag?.numero_referencia ||
    'Cliente';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="modal-visita-agenda-title"
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '90vh',
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Cabecera fija: técnico + cliente + estado, con acento del color del estado */}
      <DialogTitle id="modal-visita-agenda-title" component="div" sx={{ p: 0, borderTop: `4px solid ${color}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                bgcolor: `${color}1A`,
                color,
              }}
            >
              <EngineeringIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {tecnico}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {cliente}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <Chip label={estadoLabel} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }} />
            <IconButton onClick={onClose} aria-label="Cerrar" size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          aria-label="Secciones del detalle de la visita"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 48,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 48 },
          }}
        >
          <Tab id="tab-calidad" aria-controls="panel-calidad" icon={<FactCheckIcon />} iconPosition="start" label="Calidad de Visita" />
          <Tab id="tab-ticket" aria-controls="panel-ticket" icon={<ConfirmationNumberIcon />} iconPosition="start" label="Ticket" />
        </Tabs>
      </DialogTitle>

      {/* Solo el contenido hace scroll; la cabecera + tabs quedan fijas */}
      <DialogContent sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#f8fafc' }}>
        <div role="tabpanel" id="panel-calidad" aria-labelledby="tab-calidad" hidden={tab !== 0}>
          {tab === 0 &&
            (idTicket ? (
              <CalidadVisitaPanel idTicket={idTicket} />
            ) : (
              <PanelState
                icon={<FactCheckIcon sx={{ fontSize: 56, opacity: 0.35 }} />}
                title="Sin visita registrada"
                msg="Esta visita no tiene un ticket / orden de trabajo asociado para mostrar su calidad."
              />
            ))}
        </div>
        <div role="tabpanel" id="panel-ticket" aria-labelledby="tab-ticket" hidden={tab !== 1}>
          {tab === 1 &&
            (idTicket ? (
              <TicketDetailContent ticketId={idTicket} embedded readOnly />
            ) : (
              <PanelState
                icon={<ConfirmationNumberIcon sx={{ fontSize: 56, opacity: 0.35 }} />}
                title="Sin ticket asociado"
                msg="Esta visita no tiene un ticket de soporte ligado."
              />
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
